import { AssetAllocation, AssetAllocationId, AssetPercentages, CashAllocation, Portfolio } from '@qvs/core';
import { isEmpty, isNil } from 'lodash-es';
import { environment } from '#environment';
import { dynamicAssetColorMap, staticAssetColorMap } from '~/shared/helpers/assetColorsMap';
import { dynamicAssetLabels, staticAssetLabels } from '~/shared/helpers/assetLabelsMap';

const assetIdsWithLightPercentageChipBackground: Set<AssetAllocationId> = new Set([
  AssetAllocationId.FIXED_INCOME_LEVEL1,
  AssetAllocationId.MIXED_FUNDS_LEVEL1,
  AssetAllocationId.REAL_ESTATE_LEVEL1
]);

export class AssetHelper {
  //Can compare to total or another asset.
  static percentageOf(asset: AssetAllocation, target: number) {
    if (asset === null || target === null) {
      return 0;
    }
    if (isNil(asset?.total?.value) || asset?.total?.value === 0) {
      return 0;
    }
    const ratio = asset?.total?.value / target;
    return ratio * 100;
  }

  static getCashAllocation(assetAllocations: AssetAllocation[]): CashAllocation {
    return !!assetAllocations ? assetAllocations.find((asset) => AssetAllocationId.CASH.includes(asset.id)) : undefined;
  }

  static getPercentages(portfolio: Portfolio): AssetPercentages {
    if (isEmpty(portfolio?.assetAllocations)) {
      return undefined;
    }

    const paddingFactor = 1;
    const percentages: AssetPercentages = {};

    portfolio.assetAllocations.forEach((allocation) => {
      percentages[allocation.id] = paddingFactor * AssetHelper.percentageOf(allocation, portfolio.total.value);
    });
    return percentages;
  }

  static getAssetColor(id: AssetAllocationId, light = false): string {
    const assetsColorMap = environment.featureFlags.dynamicAssets ? dynamicAssetColorMap : staticAssetColorMap;
    const assetsColors = light ? assetsColorMap.light : assetsColorMap.default;
    return assetsColors.get(id);
  }

  static getAssetPercentageChipTextColor(id?: AssetAllocationId): string {
    return id && assetIdsWithLightPercentageChipBackground.has(id)
      ? '--portfolio-card-asset-percentage-chip-light-text-color'
      : '--white';
  }

  static getAsset(assetAllocations: AssetAllocation[], assetId: AssetAllocationId): AssetAllocation {
    return assetAllocations?.find((asset) => asset?.id === assetId);
  }

  static shouldDisplayAsset(assetId: AssetAllocationId): boolean {
    return !environment.featureFlags.dynamicAssets
      ? [
          AssetAllocationId.CASH,
          AssetAllocationId.FIXED_INCOME,
          AssetAllocationId.EQUITIES,
          AssetAllocationId.ALTERNATIVES,
          AssetAllocationId.COMMODITIES,
          AssetAllocationId.HOLDINGS
        ].includes(assetId)
      : true;
  }

  static getAssetLabel(asset: AssetAllocation): string {
    // CGE: WIZE Backend does not provide translated 'description' therefore temporarily hardcoded labels are used.
    const assetLabels = environment.featureFlags.dynamicAssets ? dynamicAssetLabels : staticAssetLabels;
    return assetLabels.get(asset?.id) ?? asset?.description;
  }
}
